import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function Settings() {
    const navigate = useNavigate();
    const [activeCard, setActiveCard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Developer login states
    const [showDevLogin, setShowDevLogin] = useState(false);
    const [devUsername, setDevUsername] = useState('');
    const [devPassword, setDevPassword] = useState('');
    const [devLoginError, setDevLoginError] = useState('');
    const [devLoginLoading, setDevLoginLoading] = useState(false);
    
    // Role assignments
    const [dataEntryUser, setDataEntryUser] = useState(null);
    const [verifierUser, setVerifierUser] = useState(null);
    
    // Search states
    const [dataEntrySearch, setDataEntrySearch] = useState('');
    const [verifierSearch, setVerifierSearch] = useState('');
    const [dataEntryResults, setDataEntryResults] = useState([]);
    const [verifierResults, setVerifierResults] = useState([]);
    const [selectedDataEntry, setSelectedDataEntry] = useState(null);
    const [selectedVerifier, setSelectedVerifier] = useState(null);

    const settingsCards = [
        {
            id: 'attendance-management',
            title: 'Attendance Data Management',
            description: 'Assign persons to enter and verify attendance data',
            icon: 'pi-user-edit',
            color: '#2266e3',
            bgColor: '#e8f0fe'
        },
        {
            id: 'developer-guide',
            title: 'Developer Guide',
            description: 'Technical documentation and system infrastructure',
            icon: 'pi-code',
            color: '#7c3aed',
            bgColor: '#f3e8ff'
        }
    ];

    // Fetch current role assignments
    const fetchRoleAssignments = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/settings/attendance-roles`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setDataEntryUser(data.dataEntry);
                setVerifierUser(data.verifier);
                setSelectedDataEntry(data.dataEntry);
                setSelectedVerifier(data.verifier);
                setDataEntrySearch('');
                setVerifierSearch('');
            }
        } catch (err) {
            setError('Failed to fetch role assignments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeCard === 'attendance-management') {
            fetchRoleAssignments();
        }
    }, [activeCard]);

    // Search users (teachers and staff combined)
    const searchUsers = async (query, setResults) => {
        if (query.length < 2) {
            setResults([]);
            return;
        }
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/settings/search-users?query=${encodeURIComponent(query)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setResults(data.users);
            }
        } catch (err) {
            console.error('Search error:', err);
        }
    };

    // Save role assignments
    const handleSaveAssignments = async () => {
        if (!selectedDataEntry || !selectedVerifier) {
            setError('Please select both Data Entry person and Verifier');
            return;
        }
        
        setSaveLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE_URL}/settings/attendance-roles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    dataEntryUserId: selectedDataEntry.userLoginId,
                    verifierUserId: selectedVerifier.userLoginId
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setSuccess('Role assignments saved successfully');
                setDataEntryUser(selectedDataEntry);
                setVerifierUser(selectedVerifier);
            } else {
                setError(data.message || 'Failed to save assignments');
            }
        } catch (err) {
            setError('Failed to save assignments');
        } finally {
            setSaveLoading(false);
        }
    };

    // Handle card click
    const handleCardClick = (cardId) => {
        if (cardId === 'developer-guide') {
            setShowDevLogin(true);
            setDevLoginError('');
            setDevUsername('');
            setDevPassword('');
        } else {
            setActiveCard(cardId);
        }
    };

    // Developer login
    const handleDevLogin = async () => {
        if (!devUsername || !devPassword) {
            setDevLoginError('Please enter username and password');
            return;
        }
        
        setDevLoginLoading(true);
        setDevLoginError('');
        
        try {
            const response = await fetch(`${API_BASE_URL}/developer/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: devUsername,
                    password: devPassword
                })
            });
            
            const data = await response.json();
            if (data.success) {
                localStorage.setItem('developerToken', data.token);
                localStorage.setItem('developerUser', JSON.stringify(data.user));
                setShowDevLogin(false);
                navigate('/developer-guide');
            } else {
                setDevLoginError(data.message || 'Invalid credentials');
            }
        } catch (err) {
            setDevLoginError('Login failed. Please try again.');
        } finally {
            setDevLoginLoading(false);
        }
    };

    const renderCardContent = () => {
        if (!activeCard) return null;

        switch (activeCard) {
            case 'attendance-management':
                return (
                    <div className="settings-panel">
                        <div className="panel-header">
                            <i className="pi pi-user-edit" style={{ color: '#2266e3' }}></i>
                            <h3>Attendance Data Management</h3>
                            <button className="close-btn" onClick={() => setActiveCard(null)}>
                                <i className="pi pi-times"></i>
                            </button>
                        </div>
                        
                        <div className="panel-content">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                                    <p>Loading assignments...</p>
                                </div>
                            ) : (
                                <>
                                    {success && (
                                        <Message severity="success" text={success} style={{ width: '100%', marginBottom: '16px' }} />
                                    )}
                                    {error && (
                                        <Message severity="error" text={error} style={{ width: '100%', marginBottom: '16px' }} />
                                    )}
                                    
                                    <div className="info-box" style={{ marginBottom: '20px' }}>
                                        <i className="pi pi-info-circle"></i>
                                        <span>Assign one person for data entry and one for verification. Search from teachers or non-academic staff.</span>
                                    </div>
                                    
                                    {/* Data Entry Person */}
                                    <div className="role-assignment-section">
                                        <h4><i className="pi pi-pencil" style={{ color: '#ea580c', marginRight: '8px' }}></i>Data Entry Person</h4>
                                        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '12px' }}>
                                            This person can manually enter or adjust attendance records
                                        </p>
                                        
                                        {dataEntryUser && (
                                            <div className="current-assignment">
                                                <span className="label">Current:</span>
                                                <span className="value">{dataEntryUser.name} ({dataEntryUser.username})</span>
                                            </div>
                                        )}
                                        
                                        <div style={{ position: 'relative' }}>
                                            <InputText
                                                value={dataEntrySearch}
                                                onChange={(e) => {
                                                    setDataEntrySearch(e.target.value);
                                                    searchUsers(e.target.value, setDataEntryResults);
                                                }}
                                                placeholder="Search to change assignment..."
                                                style={{ width: '100%' }}
                                            />
                                            {dataEntryResults.length > 0 && (
                                                <div className="search-dropdown">
                                                    {dataEntryResults.map(u => (
                                                        <div key={u.userLoginId} className="search-result-item"
                                                            onClick={() => {
                                                                setSelectedDataEntry(u);
                                                                setDataEntrySearch('');
                                                                setDataEntryResults([]);
                                                            }}
                                                        >
                                                            <strong>{u.name}</strong>
                                                            <span className="user-type">{u.type}</span>
                                                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                                {u.tid ? `TID: ${u.tid}` : `Emp: ${u.employeeNo}`} | {u.username}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {selectedDataEntry && selectedDataEntry.userLoginId !== dataEntryUser?.userLoginId && (
                                            <div className="new-selection">
                                                <i className="pi pi-arrow-right"></i>
                                                New: <strong>{selectedDataEntry.name}</strong> ({selectedDataEntry.username})
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Verifier */}
                                    <div className="role-assignment-section" style={{ marginTop: '24px' }}>
                                        <h4><i className="pi pi-check-circle" style={{ color: '#0891b2', marginRight: '8px' }}></i>Verifier (Supervisor)</h4>
                                        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '12px' }}>
                                            This person can approve or reject manual attendance entries
                                        </p>
                                        
                                        {verifierUser && (
                                            <div className="current-assignment">
                                                <span className="label">Current:</span>
                                                <span className="value">{verifierUser.name} ({verifierUser.username})</span>
                                            </div>
                                        )}
                                        
                                        <div style={{ position: 'relative' }}>
                                            <InputText
                                                value={verifierSearch}
                                                onChange={(e) => {
                                                    setVerifierSearch(e.target.value);
                                                    searchUsers(e.target.value, setVerifierResults);
                                                }}
                                                placeholder="Search to change assignment..."
                                                style={{ width: '100%' }}
                                            />
                                            {verifierResults.length > 0 && (
                                                <div className="search-dropdown">
                                                    {verifierResults.map(u => (
                                                        <div key={u.userLoginId} className="search-result-item"
                                                            onClick={() => {
                                                                setSelectedVerifier(u);
                                                                setVerifierSearch('');
                                                                setVerifierResults([]);
                                                            }}
                                                        >
                                                            <strong>{u.name}</strong>
                                                            <span className="user-type">{u.type}</span>
                                                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                                {u.tid ? `TID: ${u.tid}` : `Emp: ${u.employeeNo}`} | {u.username}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {selectedVerifier && selectedVerifier.userLoginId !== verifierUser?.userLoginId && (
                                            <div className="new-selection">
                                                <i className="pi pi-arrow-right"></i>
                                                New: <strong>{selectedVerifier.name}</strong> ({selectedVerifier.username})
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
                                        <Button
                                            label="Save Assignments"
                                            icon="pi pi-save"
                                            className="p-button-primary"
                                            onClick={handleSaveAssignments}
                                            loading={saveLoading}
                                            disabled={saveLoading}
                                        />
                                        <Button
                                            label="Reset"
                                            icon="pi pi-refresh"
                                            className="p-button-outlined"
                                            onClick={fetchRoleAssignments}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="settings-container">
            <div className="settings-layout">
                <div className="settings-sidebar">
                    {settingsCards.map(card => (
                        <div
                            key={card.id}
                            className={`settings-card ${activeCard === card.id ? 'active' : ''}`}
                            onClick={() => handleCardClick(card.id)}
                        >
                            <div className="card-icon" style={{ backgroundColor: card.bgColor }}>
                                <i className={`pi ${card.icon}`} style={{ color: card.color }}></i>
                            </div>
                            <div className="card-info">
                                <h3>{card.title}</h3>
                                <p>{card.description}</p>
                            </div>
                            <i className="pi pi-chevron-right arrow-icon"></i>
                        </div>
                    ))}
                </div>
                
                <div className="settings-content">
                    {activeCard ? renderCardContent() : (
                        <div className="no-selection">
                            <i className="pi pi-cog" style={{ fontSize: '4rem', color: '#d1d5db', marginBottom: '16px' }}></i>
                            <p>Select a settings option from the left</p>
                        </div>
                    )}
                </div>
            </div>
            
            <style>{`
                .settings-container {
                    padding: 16px 16px 16px 0;
                    max-width: 1400px;
                }
                
                .settings-layout {
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                }
                
                .settings-sidebar {
                    width: 320px;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                
                .settings-content {
                    flex: 1;
                    min-width: 0;
                }
                
                .settings-card {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: white;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: 2px solid transparent;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                
                .settings-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                
                .settings-card.active {
                    border-color: #2266e3;
                    background: #f8faff;
                }
                
                .card-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .card-icon i {
                    font-size: 1.25rem;
                }
                
                .card-info {
                    flex: 1;
                    min-width: 0;
                }
                
                .card-info h3 {
                    margin: 0 0 4px 0;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                }
                
                .card-info p {
                    margin: 0;
                    font-size: 0.85rem;
                    color: #6b7280;
                }
                
                .arrow-icon {
                    color: #9ca3af;
                    font-size: 0.875rem;
                }
                
                .settings-panel {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                
                .settings-panel .panel-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }
                
                .settings-panel .panel-header i:first-child {
                    font-size: 1.25rem;
                }
                
                .settings-panel .panel-header h3 {
                    flex: 1;
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1f2937;
                }
                
                .settings-panel .close-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 8px;
                    color: #6b7280;
                    transition: all 0.2s;
                }
                
                .settings-panel .close-btn:hover {
                    background: #f3f4f6;
                    color: #1f2937;
                }
                
                .settings-panel .panel-content {
                    padding: 20px;
                }
                
                .info-box {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 12px 16px;
                    background: #f0f9ff;
                    border-radius: 8px;
                    color: #0369a1;
                    font-size: 0.9rem;
                }
                
                .info-box i {
                    margin-top: 2px;
                }
                
                .role-assignment-section {
                    padding: 16px;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }
                
                .role-assignment-section h4 {
                    margin: 0 0 8px 0;
                    font-size: 1rem;
                    color: #1f2937;
                    display: flex;
                    align-items: center;
                }
                
                .current-assignment {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    background: #d1fae5;
                    border-radius: 6px;
                    margin-bottom: 12px;
                    font-size: 0.9rem;
                }
                
                .current-assignment .label {
                    color: #065f46;
                    font-weight: 500;
                }
                
                .current-assignment .value {
                    color: #047857;
                }
                
                .new-selection {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    background: #fef3c7;
                    border-radius: 6px;
                    margin-top: 12px;
                    font-size: 0.9rem;
                    color: #92400e;
                }
                
                .search-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    max-height: 200px;
                    overflow: auto;
                    z-index: 100;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .search-result-item {
                    padding: 10px 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                    transition: background 0.2s;
                }
                
                .search-result-item:hover {
                    background: #f3f4f6;
                }
                
                .search-result-item:last-child {
                    border-bottom: none;
                }
                
                .user-type {
                    display: inline-block;
                    padding: 2px 8px;
                    background: #e0e7ff;
                    color: #3730a3;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    margin-left: 8px;
                }
                
                .no-selection {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    color: #9ca3af;
                }
                
                .dev-login-content {
                    padding: 8px 0;
                }
                
                .dev-login-content .field {
                    margin-bottom: 20px;
                }
                
                .dev-login-content label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 500;
                    color: #374151;
                }
                
                .dev-login-content .p-inputtext,
                .dev-login-content .p-password {
                    width: 100%;
                }
                
                .dev-login-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                
                .dev-login-header i {
                    font-size: 1.5rem;
                    color: #7c3aed;
                }
                
                .dev-login-footer {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                }
            `}</style>
            
            {/* Developer Login Dialog */}
            <Dialog
                visible={showDevLogin}
                onHide={() => setShowDevLogin(false)}
                header={
                    <div className="dev-login-header">
                        <i className="pi pi-code"></i>
                        <span>Developer Access</span>
                    </div>
                }
                style={{ width: '420px' }}
                modal
                closable
                draggable={false}
            >
                <div className="dev-login-content">
                    <div style={{ 
                        background: '#f3e8ff', 
                        padding: '12px 16px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <i className="pi pi-lock" style={{ color: '#7c3aed' }}></i>
                        <span style={{ color: '#6b21a8', fontSize: '0.9rem' }}>
                            This area is restricted to authorized developers only.
                        </span>
                    </div>
                    
                    {devLoginError && (
                        <Message severity="error" text={devLoginError} style={{ width: '100%', marginBottom: '16px' }} />
                    )}
                    
                    <div className="field">
                        <label htmlFor="dev-username">Username or NIC</label>
                        <InputText
                            id="dev-username"
                            value={devUsername}
                            onChange={(e) => setDevUsername(e.target.value)}
                            placeholder="Enter your username"
                            style={{ width: '100%' }}
                            onKeyPress={(e) => e.key === 'Enter' && handleDevLogin()}
                        />
                    </div>
                    
                    <div className="field">
                        <label htmlFor="dev-password">Password</label>
                        <Password
                            id="dev-password"
                            value={devPassword}
                            onChange={(e) => setDevPassword(e.target.value)}
                            placeholder="Enter your password"
                            feedback={false}
                            toggleMask
                            style={{ width: '100%' }}
                            inputStyle={{ width: '100%' }}
                            onKeyPress={(e) => e.key === 'Enter' && handleDevLogin()}
                        />
                    </div>
                    
                    <div className="dev-login-footer" style={{ marginTop: '24px' }}>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowDevLogin(false)}
                        />
                        <Button
                            label="Login"
                            icon="pi pi-sign-in"
                            className="p-button-primary"
                            onClick={handleDevLogin}
                            loading={devLoginLoading}
                            style={{ background: '#7c3aed', borderColor: '#7c3aed' }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default Settings;
